/**
 * Session + current-account context. Everything account-scoped reads `useAccount()`;
 * the selected account id is remembered per browser.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { Session } from '@/api/api';
import type { Me, MemberRole, MyAccount } from '@/api/types';
import { qk } from '@/api/keys';

// ── Auth ───────────────────────────────────────────────────────────────────
interface AuthState { session: Session | null; ready: boolean }
const AuthCtx = createContext<AuthState>({ session: null, ready: false });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ session: null, ready: false });
  const qc = useQueryClient();
  useEffect(() => {
    let alive = true;
    api.getSession().then((session) => { if (alive) setState({ session, ready: true }); }).catch(() => { if (alive) setState({ session: null, ready: true }); });
    const off = api.onAuthChange((session) => {
      setState({ session, ready: true });
      if (!session) qc.clear();
      else void qc.invalidateQueries();
    });
    return () => { alive = false; off(); };
  }, [qc]);
  return <AuthCtx.Provider value={state}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  return useContext(AuthCtx);
}

// ── Account ────────────────────────────────────────────────────────────────
export type Area = 'listings' | 'leads' | 'campaigns' | 'billing' | 'analytics' | 'storefront' | 'team' | 'settings';

const ROLE_AREAS: Record<MemberRole, Area[]> = {
  owner: ['listings', 'leads', 'campaigns', 'billing', 'analytics', 'storefront', 'team', 'settings'],
  admin: ['listings', 'leads', 'campaigns', 'billing', 'analytics', 'storefront', 'team', 'settings'],
  sales: ['listings', 'leads', 'analytics'],
  billing: ['billing', 'analytics'],
};

interface AccountState {
  me: Me;
  accounts: MyAccount[];
  account: MyAccount;
  accountId: string;
  role: MemberRole;
  isStaff: boolean;
  can: (area: Area) => boolean;
  switchAccount: (id: string) => void;
}
const AccountCtx = createContext<AccountState | null>(null);
const KEY = 'flyt.account';

export function useMeQuery() {
  const { session } = useAuth();
  return useQuery({ queryKey: qk.me, queryFn: () => api.me(), enabled: !!session, staleTime: 5 * 60_000 });
}
export function useMyAccountsQuery() {
  const { session } = useAuth();
  return useQuery({ queryKey: qk.myAccounts, queryFn: () => api.myAccounts(), enabled: !!session, staleTime: 60_000 });
}

export function AccountProvider({ me, accounts, children }: { me: Me; accounts: MyAccount[]; children: ReactNode }) {
  const [selected, setSelected] = useState<string | null>(() => {
    try { return localStorage.getItem(KEY); } catch { return null; }
  });
  const account = accounts.find((a) => a.id === selected) ?? accounts[0];
  const switchAccount = useCallback((id: string) => {
    setSelected(id);
    try { localStorage.setItem(KEY, id); } catch { /* ignore */ }
  }, []);
  const value = useMemo<AccountState>(() => ({
    me, accounts, account, accountId: account.id, role: account.role, isStaff: me.is_staff,
    can: (area) => ROLE_AREAS[account.role].includes(area),
    switchAccount,
  }), [me, accounts, account, switchAccount]);
  return <AccountCtx.Provider value={value}>{children}</AccountCtx.Provider>;
}

export function useAccount(): AccountState {
  const c = useContext(AccountCtx);
  if (!c) throw new Error('useAccount outside AccountProvider');
  return c;
}

/** For pages rendered both inside and outside the shell (e.g. onboarding). */
export function useOptionalAccount(): AccountState | null {
  return useContext(AccountCtx);
}
